/**
 * Dashboard Clients Admin
 * Source : customerOrgs (liste, count, inactifs). Période pour filtre inactifs.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Home } from 'lucide-react';
import { Card, CardBody } from '@/components/ui';
import { useAuth } from '@/app/providers';
import { getOrganizationId } from '@/shared/lib/brandUtils';
import {
    listCustomerOrgs,
    countCustomerOrgs,
    countInactiveCustomerOrgs,
    type CustomerOrgView,
    type CustomerOrgsFilter,
} from '@/services/firebase/customer-orgs-repository';
import type { DocumentSnapshot } from 'firebase/firestore';

import { CustomersList } from './CustomersList';
import { CustomerDetail } from './CustomerDetail';
import { PERIOD_OPTIONS, type AnalysisPeriod, type CustomerOrgsSortField } from './types';
import { getPeriodStartDate } from './customerUtils';

export const CustomersDashboard: React.FC = () => {
    const { currentUser } = useAuth();
    const orgId = getOrganizationId();

    const [period, setPeriod] = useState<AnalysisPeriod>(30);
    const [sort, setSort] = useState<{ field: CustomerOrgsSortField; direction: 'asc' | 'desc' }>({
        field: 'lastPaidAt',
        direction: 'desc',
    });
    const [filter, setFilter] = useState<CustomerOrgsFilter>('all');
    const [pageLimit, setPageLimit] = useState<25 | 50>(50);
    const [search, setSearch] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const pageCursorsRef = useRef<(DocumentSnapshot | null)[]>([]);

    const [items, setItems] = useState<CustomerOrgView[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [inactiveCount, setInactiveCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const [selectedCustomer, setSelectedCustomer] = useState<CustomerOrgView | null>(null);
    const [showDetail, setShowDetail] = useState(false);

    const periodStart = React.useMemo(() => getPeriodStartDate(period), [period]);
    const totalPages = Math.max(1, Math.ceil(totalCount / pageLimit));

    const fetchPage = useCallback(async (page: number, startAfterDoc?: DocumentSnapshot | null) => {
        if (!orgId) return;
        setLoading(true);
        try {
            const cursor =
                startAfterDoc !== undefined
                    ? startAfterDoc
                    : page === 1
                        ? undefined
                        : pageCursorsRef.current[page - 2] ?? undefined;
            const [listRes, total, inactive] = await Promise.all([
                listCustomerOrgs({
                    orgId,
                    sort: sort.field,
                    direction: sort.direction,
                    filter,
                    inactiveBefore: filter === 'inactive' ? periodStart : undefined,
                    pageLimit,
                    startAfterDoc: cursor ?? undefined,
                }),
                countCustomerOrgs(orgId),
                countInactiveCustomerOrgs(orgId, periodStart),
            ]);
            setItems(listRes.items);
            if (listRes.lastDoc) {
                pageCursorsRef.current[page - 1] = listRes.lastDoc;
            }
            setHasMore(listRes.hasMore);
            setTotalCount(total);
            setInactiveCount(inactive);
        } catch (e) {
            console.error('Fetch customerOrgs failed', e);
        } finally {
            setLoading(false);
        }
    }, [orgId, sort.field, sort.direction, filter, periodStart, pageLimit]);

    const skipNextFetchRef = useRef(false);

    useEffect(() => {
        if (!orgId) return;
        pageCursorsRef.current = [];
        setCurrentPage(1);
        skipNextFetchRef.current = true;
        fetchPage(1);
    }, [orgId, sort.field, sort.direction, filter, periodStart, pageLimit, fetchPage]);

    useEffect(() => {
        if (!orgId) return;
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false;
            return;
        }
        fetchPage(currentPage);
    }, [orgId, currentPage, fetchPage]);

    const handlePageChange = useCallback((nextPage: number) => {
        setCurrentPage((prev) => {
            const page = Math.max(1, Math.min(totalPages, nextPage));
            return page;
        });
    }, [totalPages]);

    const handleLoadMore = useCallback(() => {
        if (currentPage < totalPages && !loading) {
            setCurrentPage((p) => p + 1);
        }
    }, [currentPage, totalPages, loading]);

    const handleSortChange = useCallback((field: CustomerOrgsSortField, direction: 'asc' | 'desc') => {
        setSort({ field, direction });
    }, []);

    const handleFilterChange = useCallback((f: CustomerOrgsFilter) => {
        setFilter(f);
    }, []);

    const handleSelectCustomer = useCallback((c: CustomerOrgView) => {
        setSelectedCustomer(c);
        setShowDetail(true);
    }, []);

    const handleCloseDetail = useCallback(() => {
        setShowDetail(false);
        // Réinitialiser immédiatement pour éviter les problèmes de sélection
        setSelectedCustomer(null);
    }, []);

    return (
        <div className="min-h-screen bg-background-secondary pb-10">
            <div className="bg-surface border-b border-border shadow-sm">
                <div className="pt-4 sm:pt-6 pb-4 px-4 sm:px-6">
                    <nav className="flex items-center justify-end flex-wrap gap-2 sm:gap-3 text-sm">
                        <Link
                            to="/organizations/manage"
                            className="flex items-center space-x-2 px-3 py-2 rounded-lg text-primary hover:text-primary-dark hover:bg-primary-20 transition-all duration-200 font-medium group"
                        >
                            <ChevronLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                            <Home className="h-4 w-4" />
                            <span>Mes espaces</span>
                        </Link>
                        <span className="text-foreground-secondary">/</span>
                        <span className="text-foreground font-medium">Clients</span>
                    </nav>
                </div>
            </div>

            <Card variant="default" className="mt-6 mx-2 sm:mx-4 lg:ml-2.5 lg:mr-6">
                <CardBody className="px-4 sm:px-6 py-4 sm:py-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-foreground tracking-tight">Clients</h1>
                            <p className="mt-2 text-sm text-foreground-secondary font-medium">
                                Liste et profils depuis customerOrgs.
                            </p>
                        </div>
                        <div className="flex items-center gap-1 bg-background-tertiary p-1 rounded-lg border border-border">
                            {PERIOD_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => setPeriod(option.value)}
                                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${period === option.value
                                        ? 'bg-primary text-white shadow-sm'
                                        : 'text-foreground-secondary hover:text-foreground hover:bg-surface'
                                        }`}
                                >
                                    {option.shortLabel}
                                </button>
                            ))}
                        </div>
                    </div>
                </CardBody>
            </Card>

            <div className="mt-4 mx-2 sm:mx-4 lg:ml-2.5 lg:mr-6 space-y-4">
                <CustomersList
                    customers={items}
                    loading={loading}
                    onSelectCustomer={handleSelectCustomer}
                    selectedCustomerId={selectedCustomer?.id}
                    period={period}
                    sort={sort}
                    onSortChange={handleSortChange}
                    filter={filter}
                    onFilterChange={handleFilterChange}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    pageLimit={pageLimit}
                    onPageLimitChange={setPageLimit}
                    search={search}
                    onSearchChange={setSearch}
                    totalCount={totalCount}
                    inactiveCount={inactiveCount}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />

                <CustomerDetail
                    customer={selectedCustomer}
                    onClose={handleCloseDetail}
                    isOpen={showDetail}
                    period={period}
                />
            </div>
        </div>
    );
};
